import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatSortModule, Sort } from '@angular/material/sort';
import { faEraser, faMagnifyingGlass, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogContactsData } from './models/dialog-contacts-data';

import { KeyValuePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { FilterInfos } from 'src/app/controls/advanced-gridview/models/filter-infos';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { FilterHandler } from 'src/app/core/filter-handler';
import { Contact } from 'src/app/models/contact';
import { SelectionContext } from 'src/app/models/context/selection-context';
import { Customer } from 'src/app/models/customer';
import { ContactType } from 'src/app/models/enums/contact-type';
import { IAllSelectable } from 'src/app/models/interfaces/all-selectable';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { User } from 'src/app/models/user';
import { ContactsService } from 'src/app/services/contacts.service';
import { CustomersService } from 'src/app/services/customers.service';
import { UsersService } from 'src/app/services/users.service';
import { FormFocusDirective } from '../../controls/directives/form-focus.directive';
import { ContactDataType } from './models/contact-data-type';

@Component({
    selector: 'app-contacts',
    templateUrl: './contacts-dialog.component.html',
    styleUrls: ['./contacts-dialog.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        MatOptionModule,
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        FaIconComponent,
        MatInputModule,
        MatButtonModule,
        MatSortModule,
        MatChipsModule,
        MatIconModule,
        MatDialogClose,
        KeyValuePipe,
    ],
})
export class ContactsDialogComponent implements OnInit, OnDestroy, IAllSelectable, IFilterable {
    public resetFilterIcon = faEraser;
    public resetSearchIcon = faEraser;
    public searchIcon = faMagnifyingGlass;
    public addIcon = faPlus;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Enum des types de contact
     */
    public contactsType = ContactType;

    /**
     * Enum des types de media de contact.
     */
    public ContactDataType = ContactDataType;

    /**
     * Liste des types de contacts avec le nom à utiliser dans les labels
     */
    public contactsTypeLabels: Map<string, string> = new Map<ContactType, string>();

    /**
     * Type de contact sélectionné
     */
    public contactTypeSelected: ContactType = null;

    /**
     * Liste de tous les clients de l'organisation.
     */
    public customersRaw: Customer[];

    /**
     * Liste des clients de l'organisation respectant les filtres.
     */
    public filteredCustomers: Customer[] = [];

    /**
     * Definitions des colonnes du tableau des contacts.
     */
    public contactsTableColumnsDefinitions: ColumnDefinition[] = [];

    /**
     * Liste de tous les utilisateurs de l'organisation.
     */
    public usersRaw: User[];

    /**
     * Liste des utilisateurs de l'organisation respectant les filtres.
     */
    public filteredUsers: User[] = [];

    /**
     * Liste de tous les contacts de l'organisation.
     */
    public contactsRaw: Contact[];

    /**
     * Liste des contacts de l'organisation respectant les filtres.
     */
    public filteredContacts: Contact[] = [];

    /**
     * Gestionnaire des filtres de l'en-tête de la section.
     */
    public searchFilterHandler: FilterHandler;

    /**
     * Valeur du filtre courant.
     */
    public currentSort: Sort = { active: 'name', direction: 'asc' };

    /**
     * Contexte de la selection du tableau des contacts.
     */
    public selectionContext: SelectionContext;

    /**
     * Type de media de contact.
     */
    // public dataType: ContactDataType;

    /**
     * Constructeur.
     * @param customersService
     * @param usersService
     * @param contactsTableRef
     * @param cdRef
     * @param dialogRef
     * @param data
     * @param datePipe
     * @param displayLabelHelper
     */
    constructor(
        private customersService: CustomersService,
        private usersService: UsersService,
        private contactsService: ContactsService,
        private contactsTableRef: ElementRef,
        private cdRef: ChangeDetectorRef,
        public dialogRef: MatDialogRef<ContactsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogContactsData
    ) {
        // Label des types de contacts
        this.contactsTypeLabels.set(ContactType.Contact.toString(), 'Contacts');
        this.contactsTypeLabels.set(ContactType.User.toString(), 'Utilisateurs');
        this.contactsTypeLabels.set(ContactType.Customer.toString(), 'Clients');

        this.searchFilterHandler = new FilterHandler(this);
        this.selectionContext = new SelectionContext(this.cdRef, this);

        this.searchFilterHandler.initializeFilters([
            {
                columnName: 'search',
                headerText: 'search',
                type: ColumnType.Text,
                size: 0,
                filterType: FilterType.TextEquality,
                position: 0,
                isVisible: false,
                cell: null,
                tooltip: null,
                sortDisabled: false,
            },
        ]);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.contactTypeChange(ContactType.Contact);
    }

    /**
     * Détruit le composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();

        if (this.searchFilterHandler) {
            this.searchFilterHandler.dispose();
        }
    }

    /**
     * Retourne les Ids de tous les clients filtrés.
     * Implementation de IAllSelectable.
     * @returns Une liste d'identifiant de clients.
     */
    public getAllIds(): number[] {
        return this.filteredCustomers.map((customer: Customer, index: number) => index);
    }

    /**
     * Applique les filtres de colonnes et de recherche ainsi que le tri sur la liste.
     */
    public applyFilters() {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                if (!!this.customersRaw) {
                    const datas = this.customersRaw.slice();
                    this.filteredCustomers = datas.filter((x: Customer) => this.customerIsMatchFilters(x));
                    this.sort();
                }
                break;

            case ContactType.User:
                if (!!this.usersRaw) {
                    const datas = this.usersRaw.slice();
                    this.filteredUsers = datas.filter((x: User) => this.userIsMatchFilters(x));
                    this.sort();
                }
                break;

            case ContactType.Contact:
                if (!!this.contactsRaw) {
                    const datas = this.contactsRaw.slice();
                    this.filteredContacts = datas.filter((x: Contact) => this.contactIsMatchFilters(x));
                    this.sort();
                }
                break;
        }
    }

    public search(value: string) {}

    public resetSearch() {}

    /**
     * Charge les clients en mémoire.
     */
    private loadCustomers() {
        // const sortInfo: SortInfos = { columnName: 'name', direction: 'asc' };
        let filter: FilterInfos[] = [
            {
                ColumnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                FilterType: FilterType.TextInequality,
                Value: '',
                Type: ColumnType.Text,
                SelectionMultiple: false,
            },
        ];

        this.customersService
            .List(null, filter, null, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.customersRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    private loadUsers() {
        let filter: FilterInfos[] = [
            {
                ColumnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                FilterType: FilterType.TextInequality,
                Value: '',
                Type: ColumnType.Text,
                SelectionMultiple: false,
            },
        ];

        this.usersService
            .List(null, filter, null, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.usersRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    private loadContacts() {
        let filter: FilterInfos[] = [
            {
                ColumnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                FilterType: FilterType.TextInequality,
                Value: '',
                Type: ColumnType.Text,
                SelectionMultiple: false,
            },
        ];

        this.contactsService
            .List(null, filter, null, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.contactsRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    /**
     * Trie les clients.
     */
    private sort(): void {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                const customers = this.filteredCustomers.slice();
                this.filteredCustomers = customers.sort((a, b) => {
                    const isAsc = this.currentSort.direction === 'asc';

                    switch (this.currentSort.active) {
                        case 'code': {
                            return this.compare(a.code, b.code, isAsc);
                        }
                        case 'name': {
                            return this.compare(a.name, b.name, isAsc);
                        }
                        case 'email': {
                            return this.compare(a.email, b.email, isAsc);
                        }
                        case 'mobileNumner': {
                            return this.compare(a.mobileNumber, b.mobileNumber, isAsc);
                        }
                        case 'siret': {
                            return this.compare(a.siret, b.siret, isAsc);
                        }
                        default: {
                            return this.compare(a.name, b.name, isAsc);
                        }
                    }
                });
                break;

            case ContactType.User:
                const users = this.filteredUsers.slice();
                this.filteredUsers = users.sort((a, b) => {
                    const isAsc = this.currentSort.direction === 'asc';

                    switch (this.currentSort.active) {
                        case 'lastName': {
                            return this.compare(a.lastName, b.lastName, isAsc);
                        }
                        case 'firstName': {
                            return this.compare(a.firstName, b.firstName, isAsc);
                        }
                        case 'email': {
                            return this.compare(a.email, b.email, isAsc);
                        }
                        case 'mobileNumber': {
                            return this.compare(a.mobileNumber, b.mobileNumber, isAsc);
                        }
                        default: {
                            return this.compare(a.lastName, b.lastName, isAsc);
                        }
                    }
                });
                break;

            case ContactType.Contact:
                const contacts = this.filteredContacts.slice();
                this.filteredContacts = contacts.sort((a, b) => {
                    const isAsc = this.currentSort.direction === 'asc';

                    switch (this.currentSort.active) {
                        case 'lastName': {
                            return this.compare(a.lastName, b.lastName, isAsc);
                        }
                        case 'firstName': {
                            return this.compare(a.firstName, b.firstName, isAsc);
                        }
                        case 'email': {
                            return this.compare(a.email, b.email, isAsc);
                        }
                        case 'mobileNumber': {
                            return this.compare(a.mobileNumber, b.mobileNumber, isAsc);
                        }
                        default: {
                            return this.compare(a.lastName, b.lastName, isAsc);
                        }
                    }
                });
                break;
        }
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelé lorsque l'utilisateur clique sur l'en-tête d'une colonne du tableau des clients.
     * @param sort : information sur le tri.
     */
    public onSorting(sort: Sort) {
        this.currentSort = sort;
        this.sort();
    }

    /**
     * Appelé lorsque l'utilisateur clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    public onMouseClick(event: MouseEvent, index: number) {
        this.selectionContext.onMouseClick(event, index);
    }

    /**
     * Appelé lorsque l'utilisateur appuie sur une touche du clavier.
     * @param event Événement du clavier.
     */
    @HostListener('document:keydown', ['$event'])
    private handleKeyboardEvent(event: KeyboardEvent) {
        if (event.cancelBubble === false) {
            this.selectionContext.handleKeyboardEvent(event);
        }
    }

    /**
     * Appelé lorsque l'utilisateur clique sur un élément quelconque de la page.
     * @param event Evenment clic de la souris.
     */
    @HostListener('document:click', ['$event'])
    private clickout(event) {
        if (this.selectionContext.selections.length && !this.contactsTableRef.nativeElement.contains(event.target)) {
            // Si la selection n'est pas vide et que l'utilisateur clique en dehors du containeur des éléments pouvant être glisser/déplacer, alors vide la selection.
            this.selectionContext.clearSelection();
        }
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'TO'
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Destinataires'.
     */
    public addToAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (this.data.dataType === ContactDataType.Email) {
                if (!this.data.toAddresses.includes(this.getEmail(index))) {
                    this.data.toAddresses.push(this.getEmail(index));
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (!this.data.toAddresses.includes(this.getMobileNumber(index))) {
                    this.data.toAddresses.push(this.getMobileNumber(index));
                }
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Destinataires'.
     * @param address: Adresse email a retirer.
     */
    public removeToAddress(address: string): void {
        this.data.toAddresses.splice(this.data.toAddresses.indexOf(address), 1);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'CC'
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Copies conformes'.
     */
    public addCcAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (!this.data.ccAddresses.includes(this.getEmail(index))) {
                this.data.ccAddresses.push(this.getEmail(index));
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Copies conformes'.
     * @param address: Adresse email a retirer.
     */
    public removeCcAddress(address: string): void {
        this.data.ccAddresses.splice(this.data.ccAddresses.indexOf(address), 1);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'CCi''
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Copies conformes cachées'.
     */
    public addCciAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (!this.data.cciAddresses.includes(this.getEmail(index))) {
                this.data.cciAddresses.push(this.getEmail(index));
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Copies conformes cachées'.
     * @param address: Adresse email a retirer.
     */
    public removeCciAddress(address: string): void {
        this.data.cciAddresses.splice(this.data.cciAddresses.indexOf(address), 1);
    }

    /**
     * Indique si l'élément specifié est selectionné.
     * @param index : index de l'élément à tester
     * @returns True si l'élément est selectionné, ou false autrement.
     */
    public isSelected(index: number): boolean {
        return this.selectionContext.isSelected(index);
    }

    /**
     * Indique si un client respecte les filtres de colonnes ou de recherche.
     * @param customer : client à tester.
     * @returns True si le client respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private customerIsMatchFilters(customer: Customer): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (customer.code && customer.code.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (customer.name && customer.name.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (customer.email && customer.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (customer.mobileNumber && customer.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }

            if (customer.siret && customer.siret.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            return false;
        } else {
            return true;
        }
    }

    /**
     * Indique si un utilisateur respecte les filtres de colonnes ou de recherche.
     * @param user : utilisateur à tester.
     * @returns True si l'utilisateur respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private userIsMatchFilters(user: User): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (user.lastName && user.lastName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (user.firstName && user.firstName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (user.email && user.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (user.mobileNumber && user.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }
            return false;
        } else {
            return true;
        }
    }

    /**
     * Indique si un contact respecte les filtres de colonnes ou de recherche.
     * @param contact : contact à tester.
     * @returns True si le contact respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private contactIsMatchFilters(contact: Contact): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (contact.lastName && contact.lastName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (contact.firstName && contact.firstName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (contact.email && contact.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (contact.mobileNumber && contact.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }

            if (contact.companyName && contact.companyName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            return false;
        } else {
            return true;
        }
    }

    public contactTypeChange(contactType: ContactType) {
        this.contactTypeSelected = contactType;

        switch (contactType) {
            case ContactType.Customer:
                this.setCustomersTableColumnsDefinitions();
                this.loadCustomers();
                break;

            case ContactType.User:
                this.setUsersTableColumnsDefinitions();
                this.loadUsers();
                break;

            case ContactType.Contact:
                this.setContactsTableColumnsDefinitions();
                this.loadContacts();
                break;
        }
    }

    public onSearch() {
        this.applyFilters();
    }

    private setCustomersTableColumnsDefinitions() {
        this.contactsTableColumnsDefinitions = [
            {
                columnName: 'code',
                headerText: 'Code',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: 150,
                cell: null,
                position: 1,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: 'name',
                headerText: 'Nom',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 2,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                headerText: this.data.dataType === ContactDataType.Email ? 'Email' : 'N° de Mobile',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 3,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: 'siret',
                headerText: 'Siret',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 4,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
        ];
    }

    private setUsersTableColumnsDefinitions() {
        this.contactsTableColumnsDefinitions = [
            {
                columnName: 'lastName',
                headerText: 'Nom',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: 150,
                cell: null,
                position: 1,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: 'firstName',
                headerText: 'Prénom',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 2,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                headerText: this.data.dataType === ContactDataType.Email ? 'Email' : 'N° de Mobile',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 3,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
        ];
    }

    private setContactsTableColumnsDefinitions() {
        this.contactsTableColumnsDefinitions = [
            {
                columnName: 'lastName',
                headerText: 'Nom',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: 150,
                cell: null,
                position: 1,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: 'firstName',
                headerText: 'Prénom',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 2,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: this.data.dataType === ContactDataType.Email ? 'email' : 'mobileNumber',
                headerText: this.data.dataType === ContactDataType.Email ? 'Email' : 'N° de Mobile',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 3,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
            {
                columnName: 'companyName',
                headerText: 'Société',
                type: ColumnType.Text,
                filterType: FilterType.TextEquality,
                size: null,
                cell: null,
                position: 4,
                isVisible: true,
                tooltip: null,
                sortDisabled: false,
            },
        ];
    }

    private getEmail(index: number): string {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                return this.filteredCustomers[index].email;

            case ContactType.User:
                return this.filteredUsers[index].email;

            case ContactType.Contact:
                return this.filteredContacts[index].email;
        }
    }

    private getMobileNumber(index: number): string {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                return this.filteredCustomers[index].mobileNumber;

            case ContactType.User:
                return this.filteredUsers[index].mobileNumber;

            case ContactType.Contact:
                return this.filteredContacts[index].mobileNumber;
        }
    }

    /**
     * Comparaison pour le tri
     * @param a : valeur 1
     * @param b : valeur 2
     * @param isAsc : indique si le trie est asc ou desc
     * @returns : retourne la valeur de la comparaison
     */
    public compare(a: number | string | Date, b: number | string | Date, isAsc: boolean): number {
        if (!a) {
            return -1 * (isAsc ? 1 : -1);
        } else if (!b) {
            return 1 * (isAsc ? 1 : -1);
        }

        if (typeof a === 'string' && typeof b === 'string') {
            if (isAsc === true) {
                return (a as string).localeCompare(b as string, 'fr-FR', {
                    sensitivity: 'base',
                });
            } else {
                return (b as string).localeCompare(a as string, 'fr-FR', {
                    sensitivity: 'base',
                });
            }
        } else {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }
}
